import { useEffect, useRef, useState, DragEvent } from 'react';
import { GripIcon, Icon } from './icons';

/**
 * CommentRow renders a single `note` or `limitation` prose blob
 * attached to a template component (editor) or an inspection
 * component (capture). Multi-line textarea, auto-grows with content,
 * commits on blur, and supports drag-reorder via the grip handle.
 *
 * Limitations get an amber accent to signal scope-exclusion semantics
 * (matches Spectora's "Areas Not Fully Inspected" styling from the
 * competitive analysis at docs/research/competitive-report-analysis.md).
 *
 * Optional `includeToggle` prop is used on the inspection side, where
 * inspectors uncheck templated comments that don't apply to this day's
 * conditions (writes `is_included=false`).
 */
interface Props {
  kind: 'note' | 'limitation';
  initialBody: string;
  onCommit: (patch: { body?: string }) => void;
  onDelete: () => void;
  /**
   * When provided, renders an include checkbox. Unchecked rows render
   * dimmed so the inspector can see what they've excluded at a glance.
   * Used on the inspection side; template editor omits this.
   */
  includeToggle?: {
    included: boolean;
    onToggle: (included: boolean) => void;
  };
  /** Inspector-side only — 'template' rows can't be renamed-in-body-only, but we let them. Kept for forward-compat. */
  source?: 'template' | 'custom';
  dragHandleProps?: {
    draggable: true;
    onDragStart: (e: DragEvent) => void;
    onDragEnd: () => void;
  };
  dragActive?: boolean;
  dragOver?: boolean;
}

export function CommentRow({
  kind,
  initialBody,
  onCommit,
  onDelete,
  includeToggle,
  source,
  dragHandleProps,
  dragActive,
  dragOver,
}: Props) {
  const [body, setBody] = useState(initialBody);
  const [bodyErr, setBodyErr] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setBody(initialBody);
  }, [initialBody]);

  // Grow the textarea to fit content. Using scrollHeight avoids a fixed row
  // count locking multi-paragraph limitations into a tiny window.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [body]);

  const commit = () => {
    const trimmed = body.trim();
    if (!trimmed) {
      setBodyErr(true);
      setBody(initialBody);
      setTimeout(() => setBodyErr(false), 350);
      return;
    }
    if (trimmed !== initialBody) onCommit({ body: trimmed });
  };

  const cls = ['ts-cmt', `ts-cmt--${kind}`];
  if (includeToggle && !includeToggle.included) cls.push('ts-cmt--excluded');
  if (dragActive) cls.push('ts-row--dragging');
  if (dragOver) cls.push('ts-row--drop-target');

  return (
    <div className={cls.join(' ')}>
      <span
        className="ts-grip"
        aria-label={`Drag to reorder ${kind}`}
        style={{ width: 20, height: 20 }}
        {...(dragHandleProps ?? {})}
      >
        <GripIcon size={12} />
      </span>

      {includeToggle && (
        <label className="ts-cmt-include" aria-label={`Include this ${kind} in the report`}>
          <input
            type="checkbox"
            checked={includeToggle.included}
            onChange={(e) => includeToggle.onToggle(e.target.checked)}
          />
        </label>
      )}

      {kind === 'limitation' && (
        <span className="ts-cmt-icon" aria-hidden="true">
          <Icon name="alert" size={14} />
        </span>
      )}

      <textarea
        ref={ref}
        className={'ts-cmt-body' + (bodyErr ? ' ts-err' : '')}
        value={body}
        placeholder={kind === 'limitation' ? 'Describe the scope exclusion…' : 'Observation…'}
        aria-label={kind === 'limitation' ? 'Limitation text' : 'Note text'}
        rows={1}
        onChange={(e) => setBody(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setBody(initialBody);
            (e.target as HTMLTextAreaElement).blur();
          }
          // Cmd/Ctrl+Enter commits; plain Enter inserts newline.
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        data-source={source}
      />

      <button
        type="button"
        className="ts-cmt-del"
        aria-label={`Delete ${kind}`}
        onClick={onDelete}
      >
        <Icon name="x" size={12} />
      </button>
    </div>
  );
}
